import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { LotusLobbyGroupConfig } from '../../constants/lotusCasinoLobbyGroups'
import { getLotusLobbyMoreGamesPath } from '../../constants/lotusCasinoLobbyGroups'
import { useCasinoUi } from '../../context/CasinoUiContext'
import { useLotusLobbyGroupGames } from '../../hooks/useLotusLobbyGroupGames'
import { getGameId } from '../../utils/lotusCasinoGameFilter'
import { LOTUS_MOBILE_MQ } from '../../utils/lotusBreakpoints'
import LotusCasinoGameCard from '../LotusCasinoGameCard/LotusCasinoGameCard'

export type LotusCasinoLobbyRowProps = {
  group: LotusLobbyGroupConfig
  preLoginMode?: boolean
  maxGames?: number
  showMoreCard?: boolean
  /** Mobile lobby/Evolution: 6 games + More Games (desktop unchanged). */
  mobileLobbyLayout?: boolean
  onMoreGames?: (tabIndex: number, groupId?: number) => void
}

function useCasinoMobileLayout() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(LOTUS_MOBILE_MQ).matches : false,
  )
  useEffect(() => {
    const mq = window.matchMedia(LOTUS_MOBILE_MQ)
    const onChange = () => setIsMobile(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return isMobile
}

const LotusCasinoLobbyRow: React.FC<LotusCasinoLobbyRowProps> = ({
  group,
  preLoginMode,
  maxGames,
  showMoreCard = true,
  mobileLobbyLayout = false,
  onMoreGames,
}) => {
  const isCasinoMobile = useCasinoMobileLayout()
  const useMobileSix = mobileLobbyLayout && isCasinoMobile
  const resolvedMaxGames = useMobileSix ? 6 : maxGames
  const resolvedShowMore = useMobileSix ? true : showMoreCard

  const { playGame } = useCasinoUi()
  const { games, loading } = useLotusLobbyGroupGames(group, {
    preLoginMode,
    maxGames: resolvedMaxGames,
  })

  if (!loading && games.length === 0) return null

  const moreTarget = getLotusLobbyMoreGamesPath(group)
  const skeletonCount = useMobileSix ? 6 : 5

  return (
    <div
      className={`lotus-casino-lobby-group${useMobileSix ? ' is-mobile-lobby-row' : ''}`}
      id={`group-${group.groupId ?? group.id}`}
    >
      <div className="lotus-casino-lobby-group__title">
        {group.iconClass ? (
          <i className={`icon ${group.iconClass} lotus-casino-lobby-group__title-icon`} aria-hidden />
        ) : null}
        <span>{group.title}</span>
      </div>
      <div className="lotus-casino-lobby-group__grid">
        {loading &&
          games.length === 0 &&
          Array.from({ length: skeletonCount }).map((_, i) => (
            <div key={i} className="lotus-casino-lobby-skeleton" />
          ))}
        {games.map((game, index) => (
          <LotusCasinoGameCard
            key={getGameId(game) || index}
            game={game}
            onPlay={playGame}
            compact
            imagePriority={index < 6}
            showFavorite={!preLoginMode}
          />
        ))}
        {resolvedShowMore && !loading && (
          <div className="lotus-casino-lobby-group__more-card empty-card" tabIndex={0}>
            {preLoginMode && onMoreGames ? (
              <button
                type="button"
                className="lotus-casino-lobby-group__more-inner"
                onClick={() => onMoreGames(group.tabIndex, group.groupId)}
              >
                <span>More Games</span>
                <i className="icon icon-angle-right" aria-hidden />
              </button>
            ) : (
              <Link to={moreTarget} className="lotus-casino-lobby-group__more-inner">
                <span>More Games</span>
                <i className="icon icon-angle-right" aria-hidden />
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default LotusCasinoLobbyRow
